import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { useSelector } from "react-redux";
import { ReauthenticateModal } from "components/ReauthenticateModal";
import type { AppState } from "store/reducers/Reducer";
import { isStepUpAuthenticationRequired } from "utils/stepUpAuthErrors";
import { StepUpAuthContext, type StepUpAuthContextValue } from "./useStepUpProtectedAction";

type PendingProtectedAction<T> = {
  action: () => Promise<T>;
  resolve: (value: T) => void;
  reject: (reason?: unknown) => void;
};

export const StepUpAuthProvider = ({ children }: { children: ReactNode }) => {
  const email = useSelector((state: AppState) => state.auth.email);
  const pendingRef = useRef<PendingProtectedAction<unknown> | null>(null);
  const [isReauthenticateModalOpen, setIsReauthenticateModalOpen] = useState(false);

  const runProtectedAction = useCallback(async <T,>(action: () => Promise<T>): Promise<T> => {
    try {
      return await action();
    } catch (error) {
      if (!isStepUpAuthenticationRequired(error)) {
        throw error;
      }

      if (pendingRef.current) {
        throw error;
      }

      return await new Promise<T>((resolve, reject) => {
        pendingRef.current = {
          action: action as () => Promise<unknown>,
          resolve: resolve as (value: unknown) => void,
          reject,
        };
        setIsReauthenticateModalOpen(true);
      });
    }
  }, []);

  const closeReauthenticateModal = useCallback(() => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    setIsReauthenticateModalOpen(false);
    if (pending) {
      pending.reject(new Error("Re-authentication cancelled"));
    }
  }, []);

  const completeReauthentication = useCallback(async () => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    setIsReauthenticateModalOpen(false);
    if (!pending) {
      return;
    }

    try {
      const result = await pending.action();
      pending.resolve(result);
    } catch (error) {
      pending.reject(error);
    }
  }, []);

  const value = useMemo<StepUpAuthContextValue>(
    () => ({
      runProtectedAction,
    }),
    [runProtectedAction]
  );

  return (
    <StepUpAuthContext.Provider value={value}>
      {children}
      <ReauthenticateModal
        email={email}
        isOpen={isReauthenticateModalOpen}
        onClose={closeReauthenticateModal}
        onSuccess={() => {
          void completeReauthentication();
        }}
      />
    </StepUpAuthContext.Provider>
  );
};
